// Domain-owned route containers. Do not import legacy connected-container or all-domain registries here.
import { readCmtsSession } from "../../auth/authStore.js";
import { bootstrapAuthenticatedSession } from "../../auth/authBootstrap.js";
import { useCmtsDispatch } from "@store/hooks.js";
import { authActions } from "@store/authSlice.js";
import { permissionActions } from "@store/permissionSlice.js";
import { tenantActions } from "@store/tenantSlice.js";
import { serializeReduxError } from "@store/thunkExtra.js";
import { getCmtsPersonaLandingPath } from "../../routes/cmtsPersonaLanding.js";
import { diagnosticsOf, type CmtsConnectedContainerProps } from "../domainContainerSupport.js";
import { CmtsLoginPage } from "@modules/pages/cmtsLoginPage.js";

export function CmtsLoginContainer({ runtime }: CmtsConnectedContainerProps) {
  const diagnostics = diagnosticsOf(runtime.diagnostics);
  const dispatch = useCmtsDispatch();
  return (
    <CmtsLoginPage
      runtime={runtime}
      repository={{
        ...runtime.repositories.auth,
        login: async (payload) => {
          dispatch(authActions.loginStarted());
          try {
            const result = await runtime.repositories.auth.login(payload);
            const storedSession = readCmtsSession();
            if (!storedSession) {
              throw new Error("CMTS login completed but no authenticated session was returned by the backend.");
            }
            dispatch(authActions.loginSucceeded(storedSession));
            dispatch(authActions.loginBootstrapStarted());
            const bootstrap = await bootstrapAuthenticatedSession(runtime.repositories.auth, storedSession);
            if (!bootstrap) {
              throw new Error("CMTS login completed but authenticated bootstrap could not find a stored access token.");
            }
            dispatch(tenantActions.tenantResolved(bootstrap.tenant));
            dispatch(permissionActions.permissionsLoaded({
              permissions: bootstrap.permissions,
              roles: bootstrap.roles,
            }));
            dispatch(permissionActions.menuLoaded(bootstrap.menu));
            dispatch(authActions.sessionHydrated(bootstrap.session));
            dispatch(authActions.loginBootstrapSucceeded());
            return result;
          } catch (error: unknown) {
            const serializedError = serializeReduxError(error, "Login failed.");
            dispatch(authActions.loginFailed(serializedError));
            dispatch(authActions.loginBootstrapFailed(serializedError));
            throw error;
          }
        },
      }}
      diagnostics={diagnostics}
      onLoginSuccess={() => {
        const landingPath = getCmtsPersonaLandingPath(readCmtsSession());
        window.location.replace(landingPath);
      }}
    />
  );
}
